import { formatPrice, RIYAL } from "@sirdab/foundation";
import type { Locale, MoneyHalalas } from "@sirdab/returns-shared";

/**
 * Halalas -> SAR. The ONLY currency formatting in the portal; `<Money>` wraps this and it
 * wraps foundation's `formatPrice`, the one price formatter in the repo (TOKENS.md rule 6).
 *
 * Transport is integer halalas (packages/returns-shared/src/schemas/common.ts). 24900 is 249.00 SAR.
 * Nothing upstream of display ever sees a float; `formatPrice` takes minor units.
 */
export const HALALAS_PER_RIYAL = 100;

export { RIYAL };

export function halalasToRiyals(halalas: MoneyHalalas): number {
  return halalas / HALALAS_PER_RIYAL;
}

/**
 * Latin digits in both locales. Saudi storefronts, invoices and SMS overwhelmingly print
 * prices and order references in Latin numerals, and a shopper comparing this screen to
 * their order confirmation should not have to transliterate — the "western" numerals
 * escape hatch foundation documents for exactly this kind of shopper-facing surface.
 */
function numberLocale(locale: Locale): string {
  return locale === "ar" ? "ar-SA-u-nu-latn" : "en-SA";
}

export function formatMoney(halalas: MoneyHalalas, locale: Locale): string {
  return formatPrice(halalas, { locale, numerals: "western" });
}

/** Zero is a real value — "no fee" renders as 0.00, never as an empty cell. */
export function isZeroMoney(halalas: MoneyHalalas): boolean {
  return halalas === 0;
}

export function formatCount(value: number, locale: Locale): string {
  return new Intl.NumberFormat(numberLocale(locale)).format(value);
}

/** ISO-8601 UTC in transport; rendered in the shopper's locale for display only. */
export function formatDate(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(numberLocale(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

export function formatDateTime(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(numberLocale(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
